import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MeasuredDataWaveCardComponent } from "../wave-card/measured-data-wave-card.component";
import { RecordData } from "src/app/types";
import { RecordingEvent } from "../../measured-data-details.types";
import { PlayerSnapshot } from "../../services/player-controller.service";
import { buildMediaThumbnailUrl } from "@app/shared/utils/media-thumbnail.utils";

@Component({
  selector: "app-measured-data-video-card",
  templateUrl: "./measured-data-video-card.component.html",
  styleUrls: ["./measured-data-video-card.component.scss"],
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MeasuredDataWaveCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuredDataVideoCardComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly miniPlayerTopHiddenRatio = 1 / 3;

  @Input() mediaRecordData: any;
  @Input() recordData: any;
  /** PlayerController の snapshot signal 値。設定時は個別 video input より優先。 */
  @Input() playerSnapshot: PlayerSnapshot | null = null;
  /**
   * mediaRecordData（getMedia 結果）がまだ到着していない間 true。
   * カードを常時描画し、データ到着前から統一されたローディング表示を出すための入力。
   * 親（protocol-vs）の zone-skeleton を廃し、ローディングの単一オーナーをカードに集約する。
   */
  @Input() mediaLoading = false;
  // wave props
  @Input() showWaveCard = false;
  @Input() waveRecordData: RecordData | null = null;
  @Input() waveRecordType: number | null = null;
  @Input() recordLoadingSuccess = false;
  @Input() waveProcessing = false;
  @Input() waveDownloading = false;
  @Input() waveDownloadPercent: number | null = null;
  @Input() waveLoadingPercent: number | null = null;
  @Input() waveErrorMessage: string | null = null;
  @Input() selectedEvent: RecordingEvent | null = null;
  @Input() currentStartOffset = 0;
  @Input() playing = false;
  @Input() timelineOffsetReference = 0;
  @Input() zoomValue = 0;
  // video playback state (fallback - playerSnapshot が優先)
  @Input() videoPlaying = false;
  @Input() videoCurrentTime = 0;
  @Input() videoDuration = 0;
  @Input() displayCurrentTime?: number;
  @Input() displayDuration?: number;
  @Input() videoPlaybackRate = 1;
  @Input() videoMuted = false;
  /**
   * 表示用ミュート状態の上書き。VS(WAV-master) では聞こえる音は WAV なので、
   * ミュートアイコンは動画の恒久ミュート(playerSnapshot.muted)ではなく WAV の
   * 実ミュート状態を反映する必要がある。null のときは従来どおり動画状態を使う。
   */
  @Input() audioMutedOverride: boolean | null = null;
  @Input() playbackEnded = false;
  @Input() disableCustomOverlay = false;
  @Input() videoLoadFailed = false;
  @Input() videoReady = false;

  // ── snapshot ベースのアクセサ（playerSnapshot 優先、fallback は個別 input） ──

  get snapshotPlaying(): boolean {
    return this.playerSnapshot?.playing ?? this.videoPlaying;
  }
  get snapshotCurrentTime(): number {
    return this.playerSnapshot?.currentTime ?? this.videoCurrentTime;
  }
  get snapshotDuration(): number {
    return this.playerSnapshot?.duration ?? this.videoDuration;
  }
  get snapshotDisplayCurrentTime(): number {
    return this.playerSnapshot?.displayCurrentTime ?? this.displayCurrentTime ?? this.videoCurrentTime;
  }
  get snapshotDisplayDuration(): number {
    return this.playerSnapshot?.displayDuration ?? this.displayDuration ?? this.videoDuration;
  }
  get snapshotPlaybackRate(): number {
    return this.playerSnapshot?.playbackRate ?? this.videoPlaybackRate;
  }
  get snapshotMuted(): boolean {
    if (this.audioMutedOverride !== null) {
      return this.audioMutedOverride;
    }
    return this.playerSnapshot?.muted ?? this.videoMuted;
  }
  get snapshotPlaybackEnded(): boolean {
    return this.playerSnapshot?.playbackEnded ?? this.playbackEnded;
  }
  get snapshotPlaybackStarting(): boolean {
    return this.playerSnapshot?.playbackStarting ?? false;
  }
  get snapshotReady(): boolean {
    if (this.playerSnapshot) {
      return this.playerSnapshot.state === 'ready';
    }
    return this.videoReady;
  }
  get snapshotLoadFailed(): boolean {
    if (this.playerSnapshot) {
      return this.playerSnapshot.state === 'error';
    }
    return this.videoLoadFailed;
  }
  get snapshotRetrying(): boolean {
    return this.playerSnapshot?.state === 'retrying';
  }
  get snapshotLoading(): boolean {
    if (this.playerSnapshot) {
      const s = this.playerSnapshot.state;
      // idle も「準備中」として扱う。isEncoded 検知後・プレイヤー生成前/破棄後の idle で
      // シマーも映像も出ない「空白フラッシュ」を防ぐ（この getter は isEncoded ブロック内でのみ参照される）。
      return s === 'idle' || s === 'loading' || s === 'retrying';
    }
    return !this.videoReady && !this.videoLoadFailed;
  }
  get snapshotErrorDetail(): string | null {
    return this.playerSnapshot?.errorDetail ?? null;
  }
  get snapshotRetryCount(): number {
    return this.playerSnapshot?.retryCount ?? 0;
  }

  @Output() offsetToggle = new EventEmitter<boolean>();
  @Output() seek = new EventEmitter<number>();
  @Output() playPauseClicked = new EventEmitter<void>();
  @Output() zoomMinusClicked = new EventEmitter<void>();
  @Output() zoomResetClicked = new EventEmitter<void>();
  @Output() zoomPlusClicked = new EventEmitter<void>();
  @Output() waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  @Output() videoHostReady = new EventEmitter<HTMLElement>();
  @Output() videoPlayToggle = new EventEmitter<void>();
  @Output() videoSeekTo = new EventEmitter<number>();
  @Output() videoRateChange = new EventEmitter<number>();
  @Output() videoSeekRelative = new EventEmitter<number>();
  @Output() videoMuteToggle = new EventEmitter<void>();

  private videoHost?: ElementRef<HTMLDivElement>;
  private videoShell?: ElementRef<HTMLDivElement>;
  @ViewChild("videoWrapper") private videoWrapper?: ElementRef<HTMLDivElement>;
  /** フルスクリーン中か。fullscreenchange で同期し、アイコン/floating抑制に使う。 */
  isFullscreen = false;
  private visibilityObserver?: IntersectionObserver;
  private layoutCheckRafId: number | null = null;
  private layoutListenerCleanups: Array<() => void> = [];
  private layoutResizeObserver?: ResizeObserver;
  private isVideoMostlyVisible = true;
  private miniPlayerDismissed = false;

  floatingPos: { left: number; top: number } | null = null;
  floatingWidth: number | null = null;
  private dragPointerId: number | null = null;
  private dragOffset = { x: 0, y: 0 };
  private dragMoved = false;
  private resizePointerId: number | null = null;
  private resizeCorner: 'tl' | 'br' = 'tl';
  private resizeStart = { right: 0, bottom: 0, top: 0, left: 0, pointerX: 0, pointerY: 0 };
  private readonly minFloatingWidth = 200;

  @ViewChild("videoHost") set videoHostRef(
    ref: ElementRef<HTMLDivElement> | undefined,
  ) {
    this.videoHost = ref;
    if (ref && this.viewInitialized) {
      this.emitHostIfReady();
    }
  }

  @ViewChild("videoShell") set videoShellRef(
    ref: ElementRef<HTMLDivElement> | undefined,
  ) {
    this.videoShell = ref;
    if (!ref) {
      this.teardownLayoutTracking();
      this.teardownVisibilityObserver();
      this.isVideoMostlyVisible = true;
      return;
    }
    if (this.viewInitialized) {
      this.observeVideoShell();
      this.setupLayoutTracking();
      this.scheduleLayoutVisibilityCheck();
    }
  }

  @ViewChild(MeasuredDataWaveCardComponent)
  waveCard?: MeasuredDataWaveCardComponent;
  private viewInitialized = false;
  replaySpin = false;
  private wasEnded = false;
  private spinTimer: ReturnType<typeof setTimeout> | null = null;
  private thumbnailLoadFailed = false;

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.emitHostIfReady();
    this.observeVideoShell();
    this.setupLayoutTracking();
    this.scheduleLayoutVisibilityCheck();
    if (typeof document !== "undefined") {
      document.addEventListener("fullscreenchange", this.onFullscreenChange);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["mediaRecordData"] && this.viewInitialized) {
      this.thumbnailLoadFailed = false;
      this.miniPlayerDismissed = false;
      this.isVideoMostlyVisible = true;
      this.floatingPos = null;
      this.floatingWidth = null;
      this.emitHostIfReady();
      this.observeVideoShell();
      this.setupLayoutTracking();
      this.scheduleLayoutVisibilityCheck();
    }
    // Detect end-of-video transition for replay spin animation
    if (changes["playerSnapshot"] || changes["playbackEnded"] || changes["videoPlaying"]) {
      const ended = this.isEnded;
      if (ended && !this.wasEnded) {
        this.triggerReplaySpin();
      }
      this.wasEnded = ended;
      if (!this.hasPlaybackSession || this.isVideoMostlyVisible) {
        this.miniPlayerDismissed = false;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.spinTimer) clearTimeout(this.spinTimer);
    this.teardownLayoutTracking();
    this.teardownVisibilityObserver();
    if (typeof document !== "undefined") {
      document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    }
  }

  /**
   * 動画ラッパー（オーバーレイ・シークバーを内包）をフルスクリーン化する。
   * MKPlayer ホスト(.video-container)単体ではなくラッパーを対象にすることで、
   * カスタムコントロールがフルスクリーン中も表示される。
   */
  toggleFullscreen(): void {
    const el = this.videoWrapper?.nativeElement;
    if (!el || typeof document === "undefined") return;
    if (!document.fullscreenElement) {
      // ユーザー操作外/権限拒否時の rejection は無視する。
      el.requestFullscreen?.()?.catch(() => {
        /* ignore */
      });
    } else {
      document.exitFullscreen?.();
    }
  }

  private readonly onFullscreenChange = (): void => {
    const el = this.videoWrapper?.nativeElement ?? null;
    this.isFullscreen = !!el && document.fullscreenElement === el;
    this.cdr.markForCheck();
  };

  get isVideo(): boolean {
    return this.mediaRecordData?.data?.mediaType === "Video";
  }

  /** mediaRecordData 未到着のローディング相（データ到着前の統一シマー表示用）。 */
  get showMediaLoading(): boolean {
    return this.mediaLoading && !this.mediaRecordData;
  }

  get isPrepared(): boolean {
    return this.mediaRecordData?.data?.mediaStatus === "Prepared";
  }

  get isUploading(): boolean {
    const status = this.mediaRecordData?.data?.mediaStatus;
    return status === "Uploaded" || status === "Encoding";
  }

  get isEncoded(): boolean {
    return this.mediaRecordData?.data?.mediaStatus === "Encoded";
  }

  get videoThumbnailUrl(): string {
    return buildMediaThumbnailUrl(this.mediaRecordData?.data?.storage);
  }

  get showVideoThumbnail(): boolean {
    return (
      !!this.videoThumbnailUrl &&
      !this.thumbnailLoadFailed &&
      (!this.hasPlaybackSession || this.snapshotPlaybackStarting)
    );
  }

  onVideoThumbnailError(): void {
    this.thumbnailLoadFailed = true;
  }

  /** エンコード失敗・エラー・キャンセル等で動画を表示できない状態 */
  get isErrorStatus(): boolean {
    const status = (this.mediaRecordData?.data?.mediaStatus ?? "").toLowerCase();
    return status === "failed" || status === "error" || status === "canceled" || status === "cancelled";
  }

  /** メディアが削除済みの状態 */
  get isDeletedStatus(): boolean {
    return (this.mediaRecordData?.data?.mediaStatus ?? "").toLowerCase() === "deleted";
  }

  get isEnded(): boolean {
    // WAV-master(VS): 全体の終了判定はマスター(WAV)由来の `playbackEnded` を権威にする。
    // 動画スナップショットの currentTime/duration の丸めに依存しないため確実。
    if (this.playbackEnded || this.snapshotPlaybackEnded) {
      return true;
    }
    const dur = this.snapshotDisplayDuration;
    const cur = this.snapshotDisplayCurrentTime;
    return (
      Number.isFinite(dur) &&
      dur > 0 &&
      Number.isFinite(cur) &&
      !this.snapshotPlaying &&
      cur >= dur - 0.25
    );
  }

  get hasPlaybackSession(): boolean {
    return (
      this.snapshotPlaying ||
      this.snapshotPlaybackEnded ||
      this.snapshotCurrentTime > 0.1
    );
  }

  get showFloatingPlayer(): boolean {
    return (
      this.isEncoded &&
      this.snapshotReady &&
      this.hasPlaybackSession &&
      !this.isVideoMostlyVisible &&
      !this.miniPlayerDismissed &&
      !this.isFullscreen
    );
  }

  get progressPercent(): number {
    // 再生終了時はスクラバーを 100% で完結させる。WAV-master では動画は先行補正分だけ
    // 手前に留まる／MediaKind の終端付近シーク(2秒巻き戻し)を避けるため動画を末尾へ
    // シークしない。よってここは「論理的な終了」を表示で補完する。
    if (this.isEnded) return 100;
    // Use the float-precision currentTime/duration so the bar advances on
    // every rAF tick instead of stepping once per second when the rounded
    // displayCurrentTime crosses an integer boundary.
    const dur = this.snapshotDuration;
    const cur = this.snapshotCurrentTime;
    if (!dur || dur <= 0) return 0;
    return Math.min((cur / dur) * 100, 100);
  }

  /** 終了時は経過時間も duration（= 100%）として見せる（動画はシークしない）。 */
  get displayCurrentTimeForUi(): number {
    return this.isEnded ? this.snapshotDisplayDuration : this.snapshotDisplayCurrentTime;
  }

  formatTime(sec: number): string {
    if (!Number.isFinite(sec) || sec < 0) {
      return "0:00";
    }
    const totalSeconds = Math.floor(sec);
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${("0" + s).slice(-2)}`;
  }

  cycleRate(): void {
    const rates = [1, 1.25, 1.5, 2];
    const current = this.snapshotPlaybackRate;
    const idx = rates.findIndex(
      (r) => Math.abs(r - current) < 0.01,
    );
    const next = rates[(idx + 1) % rates.length];
    this.videoRateChange.emit(next);
  }

  seekRelative(delta: number): void {
    const duration = this.snapshotDuration || 0;
    const current = this.snapshotCurrentTime;
    const next = Math.min(
      Math.max(current + delta, 0),
      duration > 0 ? duration : Number.MAX_SAFE_INTEGER,
    );
    this.videoSeekTo.emit(next);
  }

  dismissFloatingPlayer(): void {
    this.miniPlayerDismissed = true;
    this.floatingPos = null;
    this.floatingWidth = null;
  }

  onResizePointerDown(event: PointerEvent, corner: 'tl' | 'br' = 'tl'): void {
    if (!this.showFloatingPlayer) return;
    event.stopPropagation();
    event.preventDefault();
    const wrapper = (event.currentTarget as HTMLElement).parentElement as HTMLElement;
    const rect = wrapper.getBoundingClientRect();
    this.resizePointerId = event.pointerId;
    this.resizeCorner = corner;
    this.resizeStart = {
      right: rect.right,
      bottom: rect.bottom,
      top: rect.top,
      left: rect.left,
      pointerX: event.clientX,
      pointerY: event.clientY,
    };
    this.floatingPos = { left: rect.left, top: rect.top };
    this.floatingWidth = rect.width;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.cdr.markForCheck();
  }

  onResizePointerMove(event: PointerEvent): void {
    if (this.resizePointerId !== event.pointerId) return;
    const isTl = this.resizeCorner === 'tl';
    const rawWidth = isTl
      ? this.resizeStart.right - event.clientX
      : event.clientX - this.resizeStart.left;
    const maxByEdge = isTl
      ? this.resizeStart.right
      : window.innerWidth - this.resizeStart.left;
    const maxByHeight = isTl
      ? this.resizeStart.bottom * (16 / 9)
      : (window.innerHeight - this.resizeStart.top) * (16 / 9);
    const upper = Math.max(this.minFloatingWidth, Math.min(maxByEdge, maxByHeight));
    const newWidth = Math.min(Math.max(rawWidth, this.minFloatingWidth), upper);
    const newHeight = newWidth * (9 / 16);
    this.floatingWidth = newWidth;
    this.floatingPos = {
      left: isTl ? this.resizeStart.right - newWidth : this.resizeStart.left,
      top: isTl ? this.resizeStart.bottom - newHeight : this.resizeStart.top,
    };
    this.cdr.markForCheck();
  }

  onResizePointerUp(event: PointerEvent): void {
    if (this.resizePointerId !== event.pointerId) return;
    const handle = event.currentTarget as HTMLElement;
    if (handle.hasPointerCapture(event.pointerId)) {
      handle.releasePointerCapture(event.pointerId);
    }
    this.resizePointerId = null;
  }

  onFloatingPointerDown(event: PointerEvent): void {
    if (!this.showFloatingPlayer) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) return;

    const wrapper = (event.currentTarget as HTMLElement);
    const rect = wrapper.getBoundingClientRect();
    this.dragPointerId = event.pointerId;
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    this.dragMoved = false;
    this.floatingPos = { left: rect.left, top: rect.top };
    wrapper.setPointerCapture(event.pointerId);
    event.preventDefault();
    this.cdr.markForCheck();
  }

  onFloatingPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    const wrapper = event.currentTarget as HTMLElement;
    const rect = wrapper.getBoundingClientRect();
    const maxLeft = Math.max(0, window.innerWidth - rect.width);
    const maxTop = Math.max(0, window.innerHeight - rect.height);
    const left = Math.min(Math.max(event.clientX - this.dragOffset.x, 0), maxLeft);
    const top = Math.min(Math.max(event.clientY - this.dragOffset.y, 0), maxTop);
    this.floatingPos = { left, top };
    this.dragMoved = true;
    this.cdr.markForCheck();
  }

  onFloatingPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    const wrapper = event.currentTarget as HTMLElement;
    if (wrapper.hasPointerCapture(event.pointerId)) {
      wrapper.releasePointerCapture(event.pointerId);
    }
    this.dragPointerId = null;
  }

  private emitHostIfReady(): void {
    if (this.isEncoded && this.videoHost?.nativeElement) {
      this.videoHostReady.emit(this.videoHost.nativeElement);
    }
    if (this.showWaveCard && this.waveCard?.waveContainer) {
      this.waveHostReady.emit(this.waveCard);
    }
  }

  private triggerReplaySpin(): void {
    this.replaySpin = true;
    if (this.spinTimer) clearTimeout(this.spinTimer);
    this.spinTimer = setTimeout(() => {
      this.replaySpin = false;
      this.cdr.markForCheck();
      this.spinTimer = null;
    }, 600);
  }

  private observeVideoShell(): void {
    this.teardownVisibilityObserver();
    const target = this.videoShell?.nativeElement;
    if (!target || typeof IntersectionObserver === "undefined") {
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        this.applyVisibilityFromRect(entry.boundingClientRect);
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    this.visibilityObserver.observe(target);
  }

  private setupLayoutTracking(): void {
    this.teardownLayoutTracking();
    const target = this.videoShell?.nativeElement;
    if (typeof window === "undefined" || !target) {
      return;
    }
    const onLayoutChange = () => this.scheduleLayoutVisibilityCheck();

    const scrollTargets = new Set<EventTarget>([
      window,
      ...this.collectScrollParents(target),
    ]);

    scrollTargets.forEach((scrollTarget) => {
      scrollTarget.addEventListener("scroll", onLayoutChange, {
        passive: true,
      });
      this.layoutListenerCleanups.push(() =>
        scrollTarget.removeEventListener("scroll", onLayoutChange),
      );
    });

    window.addEventListener("resize", onLayoutChange, { passive: true });
    this.layoutListenerCleanups.push(() =>
      window.removeEventListener("resize", onLayoutChange),
    );

    if (typeof ResizeObserver !== "undefined") {
      this.layoutResizeObserver = new ResizeObserver(() => onLayoutChange());
      this.layoutResizeObserver.observe(target);
    }
  }

  private teardownLayoutTracking(): void {
    if (this.layoutCheckRafId !== null) {
      cancelAnimationFrame(this.layoutCheckRafId);
      this.layoutCheckRafId = null;
    }
    this.layoutListenerCleanups.forEach((cleanup) => cleanup());
    this.layoutListenerCleanups = [];
    this.layoutResizeObserver?.disconnect();
    this.layoutResizeObserver = undefined;
  }

  private scheduleLayoutVisibilityCheck(): void {
    if (this.layoutCheckRafId !== null) {
      return;
    }
    this.layoutCheckRafId = requestAnimationFrame(() => {
      this.layoutCheckRafId = null;
      const rect = this.videoShell?.nativeElement?.getBoundingClientRect?.();
      if (rect) {
        this.applyVisibilityFromRect(rect);
      }
    });
  }

  private applyVisibilityFromRect(
    rect: Pick<DOMRectReadOnly, "top" | "bottom" | "height">,
  ): void {
    const { topBoundary, bottomBoundary } = this.getVisibleBounds();
    const hiddenPastTopTrigger =
      rect.height > 0 &&
      Math.max(0, topBoundary - rect.top) >=
        rect.height * this.miniPlayerTopHiddenRatio;
    const fullyAboveVisibleArea = rect.bottom <= topBoundary;
    const fullyBelowVisibleArea = rect.top >= bottomBoundary;
    const visibleEnough = !(
      hiddenPastTopTrigger ||
      fullyAboveVisibleArea ||
      fullyBelowVisibleArea
    );
    this.isVideoMostlyVisible = visibleEnough;
    if (visibleEnough) {
      this.miniPlayerDismissed = false;
    }
    this.cdr.markForCheck();
  }

  private teardownVisibilityObserver(): void {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = undefined;
    }
  }

  private collectScrollParents(target: HTMLElement): HTMLElement[] {
    const scrollParents: HTMLElement[] = [];
    let current = target.parentElement;

    while (current) {
      if (this.isScrollableElement(current)) {
        scrollParents.push(current);
      }
      current = current.parentElement;
    }

    return scrollParents;
  }

  private isScrollableElement(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    const overflowValue = [
      style.overflow,
      style.overflowY,
      style.overflowX,
      element.style.overflow,
      element.style.overflowY,
      element.style.overflowX,
    ].join(" ");
    return /(auto|scroll|overlay)/.test(overflowValue);
  }

  private getVisibleBounds(): { topBoundary: number; bottomBoundary: number } {
    const target = this.videoShell?.nativeElement;
    if (!target || typeof window === "undefined") {
      return { topBoundary: 0, bottomBoundary: Number.POSITIVE_INFINITY };
    }

    let topBoundary = 0;
    let bottomBoundary = window.innerHeight;

    for (const scrollParent of this.collectScrollParents(target)) {
      const rect = scrollParent.getBoundingClientRect();
      topBoundary = Math.max(topBoundary, rect.top);
      bottomBoundary = Math.min(bottomBoundary, rect.bottom);
    }

    return { topBoundary, bottomBoundary };
  }
}
